import { useEffect, useRef, useState } from "react";

import { cn } from "../lib/utils.js";

/**
 * Stardust effect: fine purple dust that appears only where the pointer is. It
 * gathers into a bunched ball that follows the cursor, and keeps going through a
 * click. Dust fades out after the pointer leaves; the rest of the page stays clear.
 *
 * The canvas paints the page surface token (`bg-surface-50`), so it matches the
 * current theme in light and dark mode. Dark mode adds the dust together on the
 * page; light mode paints it over, since added light would wash out on a pale page.
 *
 * An original Canvas 2D take on the look of the React Bits Stardust Cursor; no
 * third-party source is used. The canvas fills its parent, so the parent must be
 * `relative isolate`: the canvas sits at `-z-10`, behind the content. It takes no
 * pointer events, so clicks, selection and scrolling reach the page.
 */

/** Every default the effect falls back to -- pass any at the call site to change it. */
export const stardustDefaults = {
  /** Dust colours on a dark theme. */
  primary: "#5227FF",
  secondary: "#C33AB8",
  highlight: "#B58AFF",
  /** Dust colours on a light theme: deeper, so they still read on a pale page. */
  lightPrimary: "#4A1FD6",
  lightSecondary: "#B0309F",
  lightHighlight: "#7B4DFF",
  /** Radius of the hover ball in px; the ball is about twice this across. */
  hoverRadius: 60,
  /** Dots added every frame while the pointer is over the canvas, even when it rests. */
  hoverDots: 8,
  /** Ring buffer for the hover dust. When full, the oldest dots give way. */
  capacity: 5000,
  /** Share of the hover dust kept while the pointer is over an element marked `data-stardust-quiet`. */
  quietFactor: 0.1,
};

export type StardustConfig = typeof stardustDefaults;

export interface StardustBackgroundProps {
  className?: string;
  /** Overrides from `stardustDefaults`, read when the effect mounts. */
  config?: Partial<StardustConfig>;
  /** Scales the hover dust everywhere on the page, 1 = full strength. */
  hoverScale?: number;
}

type Rgb = [number, number, number];
type Theme = "dark" | "light";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Seconds lived. A particle with `life` 0 is free. */
  age: number;
  life: number;
  size: number;
  /** 0 = primary, 1 = secondary. Dust drifts toward secondary as it ages. */
  tone: number;
  glint: boolean;
  alpha: number;
  phase: number;
  /** Exponential damping, per second. */
  drag: number;
}

interface Spawn {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  drag: number;
  size: number;
  tone: number;
  alpha: number;
  glint?: boolean;
}

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const DARK_SCHEME = "(prefers-color-scheme: dark)";
const TAU = Math.PI * 2;
/** Longest simulation step, so a stalled tab never teleports particles. */
const MAX_DT = 0.05;
/** How quickly the hover ball catches up with the pointer; lower = more lag. */
const FOLLOW_RATE = 14;
/** Pixels of pointer path per extra sample, so fast strokes lay down more dots. */
const SAMPLE_SPACING = 1.4;
const MAX_SAMPLES_PER_FRAME = 32;
/** Flow strength: low, so the dust stays bunched around the pointer. */
const FLOW_SWIRL = 5;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

const newParticle = (): Particle => ({
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  age: 0,
  life: 0,
  size: 1,
  tone: 0,
  glint: false,
  alpha: 0,
  phase: 0,
  drag: 1,
});

/** Shared curling force field, so the dust moves as one slow, flowing mass. */
const flowX = (x: number, y: number, t: number, phase: number, s: number) =>
  s * (Math.sin(y * 0.011 + t * 0.9 + phase) * 0.6 + Math.cos(x * 0.008 - t * 0.6) * 0.4);
const flowY = (x: number, y: number, t: number, phase: number, s: number) =>
  s * (Math.cos(x * 0.01 - t * 0.7 + phase) * 0.6 + Math.sin(y * 0.009 + t * 0.5) * 0.4);

/** Expects `#rrggbb`. */
const hexToRgb = (hex: string): Rgb => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const mixRgb = (a: Rgb, b: Rgb, k: number): Rgb => [
  Math.round(a[0] + (b[0] - a[0]) * k),
  Math.round(a[1] + (b[1] - a[1]) * k),
  Math.round(a[2] + (b[2] - a[2]) * k),
];

/**
 * One crisp round dot, pre-rendered per colour and stamped with drawImage. `core`
 * lightens the centre toward white so each dot glints; zero keeps it pure, which
 * is what a pale page needs. The rim is a sharp, anti-aliased edge.
 */
function makeSprite(rgb: Rgb, core: number): HTMLCanvasElement {
  const [r, g, b] = rgb;
  const [cr, cg, cb] = core > 0 ? mixRgb(rgb, [255, 255, 255], core) : rgb;
  const canvas = document.createElement("canvas");
  canvas.width = 8;
  canvas.height = 8;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const dot = ctx.createRadialGradient(4, 4, 0, 4, 4, 4);
  dot.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
  dot.addColorStop(0.5, `rgba(${r},${g},${b},1)`);
  dot.addColorStop(0.85, `rgba(${r},${g},${b},0.6)`);
  dot.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = dot;
  ctx.fillRect(0, 0, 8, 8);
  return canvas;
}

/** Mirrors tokens.css: an explicit data-theme wins, otherwise the OS preference decides. */
const currentTheme = (): Theme => {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "dark" || attr === "light") return attr;
  return window.matchMedia(DARK_SCHEME).matches ? "dark" : "light";
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia(REDUCED_MOTION).matches;

export function StardustBackground({ className, config, hoverScale = 1 }: StardustBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const settings: StardustConfig = { ...stardustDefaults, ...config };
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const hoverScaleRef = useRef(hoverScale);
  hoverScaleRef.current = hoverScale;

  const [reduced, setReduced] = useState(prefersReducedMotion);
  const [theme, setTheme] = useState<Theme>(currentTheme);

  useEffect(() => {
    const mq = window.matchMedia(REDUCED_MOTION);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Follow the app's theme toggle (data-theme on <html>) and the OS preference.
  useEffect(() => {
    const sync = () => setTheme(currentTheme());
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const mq = window.matchMedia(DARK_SCHEME);
    mq.addEventListener("change", sync);
    return () => {
      observer.disconnect();
      mq.removeEventListener("change", sync);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const cfg = settingsRef.current;
    // Reduced motion paints the base only: the effect is entirely pointer-driven.
    const reducedMotion = reduced;
    const dark = theme === "dark";
    // Dark mode adds the dust together; light mode paints it over the page.
    const blend: GlobalCompositeOperation = dark ? "lighter" : "source-over";
    const primary = hexToRgb(dark ? cfg.primary : cfg.lightPrimary);
    const secondary = hexToRgb(dark ? cfg.secondary : cfg.lightSecondary);
    const highlight = hexToRgb(dark ? cfg.highlight : cfg.lightHighlight);
    const core = dark ? 0.35 : 0;
    const sprites = {
      primary: makeSprite(primary, core),
      mid: makeSprite(mixRgb(primary, secondary, 0.5), core),
      secondary: makeSprite(secondary, core),
      glint: makeSprite(highlight, core),
    };

    // Weaker devices get fewer particles and a 1x canvas instead of a sharper one.
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
    const lowPower = navigator.hardwareConcurrency <= 4 || memory <= 4;
    const dprCap = lowPower ? 1 : 1.5;
    // Scales particle counts; steps down further when frames run long (see frame).
    let quality = lowPower ? 0.6 : 1;

    let w = 0;
    let h = 0;
    const pool: Particle[] = Array.from({ length: cfg.capacity }, newParticle);
    let poolCursor = 0;
    // Pointer is in client (viewport) coordinates, so scrolling never skews it.
    // `quiet` is set while the pointer is over an element marked `data-stardust-quiet` (the sign-in card).
    const pointer = { x: 0, y: 0, seen: false, quiet: false };
    // The hover ball is in canvas coordinates and eases toward the pointer.
    const wake = { x: 0, y: 0, vx: 0, vy: 0, placed: false };
    let t = 0;
    let last = performance.now();
    let slowFrames = 0;
    let raf = 0;

    /** Writes into the ring buffer, so the oldest dots give way when it is full. */
    const emit = (s: Spawn) => {
      const p = pool[poolCursor];
      if (!p) return;
      poolCursor = (poolCursor + 1) % pool.length;
      p.x = s.x;
      p.y = s.y;
      p.vx = s.vx;
      p.vy = s.vy;
      p.age = 0;
      p.life = s.life;
      p.drag = s.drag;
      p.size = s.size;
      p.tone = s.tone;
      p.alpha = s.alpha;
      p.glint = s.glint ?? false;
      p.phase = rand(0, TAU);
    };

    /**
     * Hover dust: a bunched ball around the pointer. Specks are scattered along the path
     * just crossed and land inside a disc of `hoverRadius`, and the ball keeps trickling
     * while the pointer rests, so the cursor always carries a dense cloud.
     */
    const emitHover = (x0: number, y0: number, x1: number, y1: number, vx: number, vy: number, scale: number) => {
      const dist = Math.hypot(x1 - x0, y1 - y0);
      const samples = cfg.hoverDots + Math.ceil(dist / SAMPLE_SPACING);
      const count = Math.round(Math.min(MAX_SAMPLES_PER_FRAME, samples) * quality * scale);
      if (count < 1) return;
      // Momentum nudges the ball forward a little, never far.
      const mx = clamp(vx * 0.02, -20, 20);
      const my = clamp(vy * 0.02, -20, 20);
      for (let i = 0; i < count; i++) {
        const f = Math.random();
        const a = rand(0, TAU);
        const r = cfg.hoverRadius * Math.sqrt(Math.random());
        emit({
          x: x0 + (x1 - x0) * f + Math.cos(a) * r,
          y: y0 + (y1 - y0) * f + Math.sin(a) * r,
          vx: mx + Math.cos(a) * rand(2, 8),
          vy: my + Math.sin(a) * rand(2, 8),
          life: rand(1.6, 2.4),
          drag: rand(2.5, 3.5),
          size: rand(0.5, 1.1),
          tone: rand(0, 0.6),
          alpha: rand(0.45, 1),
          glint: Math.random() < 0.05,
        });
      }
    };

    const update = (dt: number) => {
      t += dt;

      const rect = canvas.getBoundingClientRect();
      const lx = pointer.x - rect.left;
      const ly = pointer.y - rect.top;
      const onCanvas = pointer.seen && lx >= 0 && ly >= 0 && lx <= w && ly <= h;

      if (pointer.seen && !wake.placed) {
        wake.x = lx;
        wake.y = ly;
        wake.placed = true;
      }
      if (wake.placed) {
        // The ball eases toward the pointer, so it lags a little behind the cursor.
        const px = wake.x;
        const py = wake.y;
        const k = 1 - Math.exp(-FOLLOW_RATE * dt);
        wake.x += (lx - wake.x) * k;
        wake.y += (ly - wake.y) * k;
        const vx = clamp((wake.x - px) / dt, -1200, 1200);
        const vy = clamp((wake.y - py) / dt, -1200, 1200);
        wake.vx += (vx - wake.vx) * 0.35;
        wake.vy += (vy - wake.vy) * 0.35;
        // Dust is only ever laid while the pointer is over the canvas, and less of it over the quiet elements.
        const scale = hoverScaleRef.current * (pointer.quiet ? cfg.quietFactor : 1);
        if (onCanvas) emitHover(px, py, wake.x, wake.y, wake.vx, wake.vy, scale);
      }

      for (const p of pool) {
        if (p.life <= 0) continue;
        p.age += dt;
        if (p.age >= p.life) {
          p.life = 0;
          continue;
        }
        p.vx += flowX(p.x, p.y, t, p.phase, FLOW_SWIRL) * dt;
        p.vy += flowY(p.x, p.y, t, p.phase, FLOW_SWIRL) * dt;
        const damp = Math.exp(-p.drag * dt);
        p.vx *= damp;
        p.vy *= damp;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
      }
    };

    const stamp = (sprite: HTMLCanvasElement, x: number, y: number, d: number, a: number) => {
      if (a < 0.01) return;
      ctx.globalAlpha = a;
      ctx.drawImage(sprite, x - d / 2, y - d / 2, d, d);
    };

    const pickSprite = (p: Particle, drift: number) => {
      if (p.glint) return sprites.glint;
      const tone = p.tone + drift;
      return tone < 0.34 ? sprites.primary : tone < 0.67 ? sprites.mid : sprites.secondary;
    };

    const render = () => {
      // Transparent, so the canvas's own `bg-surface-50` shows through and follows the theme.
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = blend;

      for (const p of pool) {
        if (p.life <= 0) continue;
        // Dust fades in over its first moments, then decays smoothly and shrinks slightly.
        const k = p.age / p.life;
        const a = p.alpha * Math.min(1, p.age * 6) * Math.pow(1 - k, 1.5);
        stamp(pickSprite(p, k * 0.35), p.x, p.y, p.size * (1 - 0.35 * k) * 2.4, a);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = (now: number) => {
      const raw = (now - last) / 1000;
      last = now;
      // A sustained slowdown steps the particle budget down; it never climbs back.
      if (raw > 0 && raw < 0.5) {
        slowFrames = raw > 1 / 30 ? slowFrames + 1 : Math.max(0, slowFrames - 1);
        if (slowFrames > 90 && quality > 0.5) {
          quality = Math.max(0.5, quality - 0.1);
          slowFrames = 0;
        }
      }
      if (raw > 0) update(Math.min(raw, MAX_DT));
      render();
      raf = requestAnimationFrame(frame);
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.seen = true;
      pointer.quiet = e.target instanceof Element && e.target.closest("[data-stardust-quiet]") !== null;
    };
    // A null relatedTarget means the pointer left the window entirely.
    const onOut = (e: PointerEvent) => {
      if (!e.relatedTarget) pointer.seen = false;
    };

    const onResize = () => {
      resize();
      if (reducedMotion) render();
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(canvas);
    onResize();

    if (!reducedMotion) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerout", onOut);
      raf = requestAnimationFrame(frame);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerout", onOut);
    };
  }, [reduced, theme]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 -z-10 h-full w-full bg-surface-50", className)}
    />
  );
}
